import React, { useState, useEffect, useRef } from "react";
import styles from "./MarginTool.module.css";

const MarginTool = ( { settingsOptionSelected, setSettingsOptionSelected, font } ) => {
	const apiUrl = process.env.REACT_APP_API_URL;
	const token = localStorage.getItem("authToken");
	const sliderRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const recommended_take_rate = 90;
	const recommended_price_margin_index = 5; //default to $2.50
	const [estimatedTakeRate, setEstimatedTakeRate] = useState(null);
	const [showEstimatedTakeRate, setShowEstimatedTakeRate] = useState(false);
	const [showTakeRateBlurb, setShowTakeRateBlurb] = useState(false);
	const [pegIndex, setPegIndex] = useState(recommended_price_margin_index); 
	const [isDragging, setIsDragging] = useState(false);
	const [showRecommended, setShowRecommended] = useState(false);
	const [showClickAndDrag, setShowClickAndDrag] = useState(false);
	const intervals = ["$0.00", "$0.50", "$1.00", "$1.50", "$2.00", "$2.50", "$3.00", "$3.50", "$4.00", "$4.50", "$5.00"];
	const intervalCount = intervals.length;

	useEffect(() => {
		const fetchSelectedMargin = async () => {
			try {
				const response = await fetch(`${apiUrl}/api/fetch_selected_margin/`, {
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"Authorization": `Bearer ${token}`,
					},
				});
				const data = await response.json();
				const selected_margin = data.selected_margin;
				const formatted = `$${selected_margin.toFixed(2)}`; // "$2.50"
				const index = intervals.indexOf(formatted);
				setPegIndex(index);
				setLoading(false);
			} catch (error) { console.error("Failed to fetch selected margin:", error); }
		};
		fetchSelectedMargin();
	}, []);

	//animations
	useEffect(() => {
		if (loading) { return; }
		const timer = setTimeout(() => { 
			setShowClickAndDrag(true); 
			setShowRecommended(true);
		}, 800); 
	    return () => clearTimeout(timer);
	}, [loading]);

	useEffect(() => {
		if (loading) { return; }
		const timer = setTimeout(() => { setShowEstimatedTakeRate(true); }, 1500); 
	    return () => clearTimeout(timer);
	}, [loading]);

	useEffect(() => {
		const handleMouseMove = (e) => {
			if (!isDragging) return;
			const rect = sliderRef.current.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const percent = x / rect.width;
			const newIndex = Math.round(percent * (intervalCount - 1));
			setPegIndex(Math.max(0, Math.min(intervalCount - 1, newIndex)));
			if (showClickAndDrag) { setShowClickAndDrag(false); }
		};

		const handleMouseUp = () => setIsDragging(false);

		if (isDragging) {
			window.addEventListener("mousemove", handleMouseMove);
			window.addEventListener("mouseup", handleMouseUp);
		}
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [isDragging]);

	//update selected margin on server
	useEffect(() => {
		if (!showEstimatedTakeRate) return;
		const timeout = setTimeout(() => { updateSelectedMargin(); }, 500); // debounce time (ms)
		return () => clearTimeout(timeout);
	}, [pegIndex]);

	//toggle take rate displayed as user slides toggle
	useEffect(() => {
		if (pegIndex <= recommended_price_margin_index) { setEstimatedTakeRate(90); }
		else {
			const take_rate_drop_percentage = (pegIndex - recommended_price_margin_index) * 15;
			const new_take_rate = recommended_take_rate - take_rate_drop_percentage;
			setEstimatedTakeRate(new_take_rate);
		}
	}, [pegIndex]);

	const updateSelectedMargin = async () => {
		try {
			await fetch(`${apiUrl}/api/update_selected_margin/`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ selected_margin: parseFloat(intervals[pegIndex].replace("$", ""))}),
			});
		} catch (error) { console.error("Failed to update selected margin:", error); }
	};

	const handleMouseDown = () => { setIsDragging(true); }

	const removeTakeRateBlurb = () => { setShowTakeRateBlurb(false); }

	const revealTakeRateBlurb = () => { setShowTakeRateBlurb(true); }

	return (
		<div className={`${styles.margin_tool_container} ${font} fade_in`}>
			<p className={`titles ${styles.profit_margin_title}`}>Set Profit Margin</p>
			{loading && (<img className={styles.margin_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} />)}
			{!loading && (
				<>
					<div className={styles.margin_slider_container}>
						{showClickAndDrag && (<p className={`${styles.click_and_drag} fade_in`}>Click and drag to select desired margin.</p>)}
						<div className={styles.margin_slider} ref={sliderRef}>
							<div className={styles.margin_slider_peg} style={{ left: `${(pegIndex / (intervalCount - 1)) * 100}%`, transform: 'translateX(-50%)' }} onMouseDown={handleMouseDown}></div>
						</div>
						<div className={styles.tick_bar}>
							{intervals.map((_, i) => (<div key={i} className={styles.tick}></div>))}
						</div>
						<div className={styles.prices_bar}>
						    {intervals.map((label, i) => (<span key={i} className="label">{label}</span>))}    
						</div>
						{showRecommended && (<p className={`${styles.recommended_text} fade_in`}>Recommended</p>)}
					</div>
					{showEstimatedTakeRate && (
						<div className={`fade_in`}>
							<p className={`${styles.estimated_take_rate}`}>Estimated take rate:&nbsp;&nbsp;<span style={{ fontWeight: "bold", fontSize: "20px", color: estimatedTakeRate >= 90 ? "green" : estimatedTakeRate >= 70 ? "orange" : "red" }}>{estimatedTakeRate}%</span></p>
							<img src={`${process.env.PUBLIC_URL}/images/info_icon.png`} alt="Logo" className={`${styles.estimated_take_rate_info_icon}`} onClick={() => { revealTakeRateBlurb(); }}/>
						</div>
					)}
					{showTakeRateBlurb && (
						<div className={styles.take_rate_blurb_container}>
							<img src={`${process.env.PUBLIC_URL}/images/remove_popup.png`} className={`remove_popup`} onClick={() => { removeTakeRateBlurb(); }} alt="remove_label_button_popup"/>
							<p className={styles.take_rate_blurb}>This % of customers are estimated to purchase a return label at this margin.</p>
						</div>
					)}
				</>
			)}
			<button onClick={() => setSettingsOptionSelected("settings_back")} className={`back_buttons ${styles.margin_tool_back_button}`}>
				<img src={`${process.env.PUBLIC_URL}/images/back_button_arrow.png`} className={styles.margin_tool_back_button_arrow} alt="back"/>
			</button>
		</div>
	);
};

export default MarginTool;